/**
 * Finance TV — painel 43" wall display, formato scoreboard.
 * Layout: tabela de linhas largas, texto grande, leitura a 3m.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
    PiArrowLeftBold,
    PiArrowsClockwiseBold,
    PiArrowsOutBold,
    PiCheckCircleBold,
    PiCurrencyDollarBold,
    PiTrendDownBold,
    PiTrendUpBold,
    PiWarningCircleBold,
} from 'react-icons/pi';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import BrandAvatar from '@v2/features/finance-tv/BrandAvatar';
import TvPairingScreen from '@v2/features/finance-tv/TvPairingScreen';
import type { FinanceQueueItem, FinanceTvResponse, UsdBrlRates } from '@v2/features/finance-tv/types';

const REFRESH_MS = 30_000;
const TV_TOKEN_KEY = 'buckler_tv_token';

/* ─── utils ───────────────────────────────────────────────── */
const fmtUsd = (v: number | null | undefined) =>
    v == null || v <= 0 ? '—'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);

const fmtBrl = (r: number) =>
    r.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });

const fmtDate = (iso: string | null | undefined) =>
    iso ? new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : null;

/* ─── Fetch ────────────────────────────────────────────────── */
function useFinanceTvData(tvMode: boolean, tvToken: string | null) {
    return useQuery<FinanceTvResponse>({
        queryKey: ['dashboard', 'finance-tv', tvMode, tvToken],
        queryFn: async () => {
            if (tvMode && tvToken) {
                const { data } = await axios.get('/api/tv/dashboard/finance', {
                    headers: { 'X-TV-Token': tvToken, Accept: 'application/json' },
                });
                return data as FinanceTvResponse;
            }
            const { data } = await api.get('/dashboard/finance-tv');
            return data as FinanceTvResponse;
        },
        refetchInterval: REFRESH_MS,
        enabled: !tvMode || !!tvToken,
    });
}

/* ─── USD Ticker ───────────────────────────────────────────── */
function UsdTicker({ rates }: { rates?: UsdBrlRates | null }) {
    if (!rates?.today) return null;
    const diff = rates.yesterday && rates.yesterday.rate > 0
        ? ((rates.today.rate - rates.yesterday.rate) / rates.yesterday.rate) * 100 : null;
    const up = diff != null && diff >= 0;
    return (
        <div className="flex items-center gap-2 rounded-xl border border-[#C9A84C]/30 bg-[#8B6914]/20 px-4 py-2">
            <div>
                <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#C9A84C' }}>USD/BRL</p>
                <p className="font-mono text-lg font-black tabular-nums" style={{ color: '#F0D06A' }}>
                    R$ {fmtBrl(rates.today.rate)}
                </p>
            </div>
            {diff != null && (
                <span className={cn('flex items-center gap-0.5 text-xs font-bold', up ? 'text-emerald-400' : 'text-red-400')}>
                    {up ? <PiTrendUpBold className="size-3" /> : <PiTrendDownBold className="size-3" />}
                    {up ? '+' : ''}{diff.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%
                </span>
            )}
        </div>
    );
}

/* ─── Status badge de pagamento — tamanho TV ───────────────── */
type PayStatus = 'paid' | 'pending' | 'waiting_comex' | 'not_due' | 'not_started';

const STATUS_CFG: Record<PayStatus, { bg: string; border: string; text: string; dot: string; pulse?: boolean }> = {
    paid:          { bg: 'bg-emerald-950/70', border: 'border-emerald-500/50', text: 'text-emerald-300', dot: 'bg-emerald-400' },
    pending:       { bg: 'bg-red-950/80',     border: 'border-red-500/70',     text: 'text-red-200',     dot: 'bg-red-400',     pulse: true },
    waiting_comex: { bg: 'bg-amber-950/60',   border: 'border-amber-400/40',   text: 'text-amber-300',   dot: 'bg-amber-400' },
    not_due:       { bg: 'bg-white/5',        border: 'border-white/10',       text: 'text-gray-500',    dot: 'bg-gray-600' },
    not_started:   { bg: 'bg-transparent',    border: 'border-white/5',        text: 'text-gray-700',    dot: 'bg-gray-800' },
};

function StatusBadge({
    status, label, date, amountUsd, pct,
}: {
    status: string; label: string; date?: string | null;
    amountUsd?: number | null; pct?: number | null;
}) {
    const cfg = STATUS_CFG[(status as PayStatus)] ?? STATUS_CFG.not_due;
    return (
        <div className={cn(
            'flex min-w-[160px] flex-col justify-center gap-1.5 rounded-xl border px-4 py-2.5',
            cfg.bg, cfg.border, cfg.pulse && 'animate-pulse',
        )}>
            {/* Linha superior: dot + status */}
            <div className="flex items-center gap-2">
                <span className={cn('size-2 shrink-0 rounded-full', cfg.dot)} />
                <span className={cn('text-sm font-black leading-tight', cfg.text)}>{label}</span>
            </div>

            {/* Linha inferior: valor + porcentagem */}
            {(amountUsd != null && amountUsd > 0) && (
                <div className="flex items-baseline gap-2 pl-4">
                    <span className="font-mono text-base font-black text-white">
                        {fmtUsd(amountUsd)}
                    </span>
                    {pct != null && (
                        <span
                            className="rounded-md px-1.5 py-0.5 text-[10px] font-black"
                            style={{
                                background: 'rgba(201,168,76,0.15)',
                                color: '#C9A84C',
                                border: '1px solid rgba(201,168,76,0.25)',
                            }}
                        >
                            {pct}%
                        </span>
                    )}
                </div>
            )}

            {date && <span className="pl-4 text-[10px] text-gray-500">{date}</span>}
        </div>
    );
}

/* ─── Linha do scoreboard ──────────────────────────────────── */
function ScoreRow({ item, idx }: { item: FinanceQueueItem; idx: number }) {
    const p1 = item.payments.find((p) => p.key === 'payment_1');
    const p2 = item.payments.find((p) => p.key === 'payment_2');
    const isCritical = p1?.status === 'pending' || p2?.status === 'pending';
    const isEven = idx % 2 === 0;

    return (
        <div
            className={cn(
                'relative flex items-center gap-4 px-5 py-3 transition-colors',
                isEven ? 'bg-zinc-900' : 'bg-zinc-800/40',
                isCritical && 'ring-1 ring-inset ring-red-500/20',
            )}
        >
            {/* Indicador lateral de urgência */}
            <div className={cn(
                'absolute left-0 top-0 h-full w-1',
                isCritical ? 'bg-red-500' : 'bg-transparent',
            )} />

            {/* Avatar + Identificação */}
            <div className="flex w-[260px] shrink-0 items-center gap-3 pl-2">
                <BrandAvatar
                    name={item.client}
                    logoUrl={item.client_logo_url}
                    color="#C9A84C"
                    size="md"
                    dark
                />
                <div className="min-w-0">
                    <p className="truncate text-base font-black text-white">{item.client}</p>
                    <p className="truncate text-xs text-gray-500">{item.supplier_display?.label ?? item.supplier}</p>
                </div>
            </div>

            {/* Pedido */}
            <div className="w-[100px] shrink-0 text-center">
                <p className="font-mono text-xs text-gray-600">Pedido</p>
                <p className="font-mono text-sm font-black text-gray-300">{item.order_number}</p>
            </div>

            {/* Total USD */}
            <div className="w-[130px] shrink-0 text-right">
                <p className="text-[10px] text-gray-600">Total</p>
                <p className="text-xl font-black tabular-nums text-white">{fmtUsd(item.total_usd)}</p>
            </div>

            {/* Progresso */}
            <div className="flex w-[90px] shrink-0 flex-col items-center gap-1">
                <p className={cn(
                    'text-2xl font-black tabular-nums',
                    item.payment_progress_pct >= 100 ? 'text-emerald-400' : 'text-[#C9A84C]',
                )}>
                    {item.payment_progress_pct}%
                </p>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/5">
                    <div
                        className="h-full rounded-full"
                        style={{
                            width: `${item.payment_progress_pct}%`,
                            background: item.payment_progress_pct >= 100
                                ? '#10b981'
                                : 'linear-gradient(90deg,#8B6914,#C9A84C,#F0D06A)',
                        }}
                    />
                </div>
            </div>

            {/* 1° Pagamento */}
            <div className="flex-1">
                <p className="mb-1 text-[9px] font-bold uppercase tracking-widest text-gray-600">1° Pagamento</p>
                <StatusBadge
                    status={p1?.status ?? 'not_started'}
                    label={p1?.status_label ?? '—'}
                    date={p1?.paid_at ? fmtDate(p1.paid_at) : undefined}
                    amountUsd={item.payment_1_usd}
                    pct={item.total_usd && item.payment_1_usd
                        ? Math.round((item.payment_1_usd / item.total_usd) * 100)
                        : null}
                />
            </div>

            {/* 2° Pagamento */}
            <div className="flex-1">
                <p className="mb-1 text-[9px] font-bold uppercase tracking-widest text-gray-600">2° Pagamento</p>
                <StatusBadge
                    status={p2?.status ?? 'not_due'}
                    label={p2?.status_label ?? '—'}
                    date={p2?.paid_at ? fmtDate(p2.paid_at) : undefined}
                    amountUsd={item.payment_2_usd}
                    pct={item.total_usd && item.payment_2_usd
                        ? Math.round((item.payment_2_usd / item.total_usd) * 100)
                        : null}
                />
            </div>

            {/* Invoice */}
            {item.invoice && (
                <div className="shrink-0">
                    <p className="text-[9px] text-gray-600">Invoice</p>
                    <p className="font-mono text-xs text-[#C9A84C]">{item.invoice}</p>
                </div>
            )}
        </div>
    );
}

/* ─── PÁGINA PRINCIPAL ─────────────────────────────────────── */
type Props = { tvMode?: boolean };

export default function FinanceTvPage({ tvMode = false }: Props) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [clock, setClock] = useState(() => new Date());
    const [isFullscreen, setIsFullscreen] = useState(false);

    const [tvToken, setTvToken] = useState<string | null>(() =>
        tvMode ? (localStorage.getItem(TV_TOKEN_KEY) ?? null) : null
    );
    const isPaired = !tvMode || !!tvToken;

    const handleApproved = useCallback((token: string) => {
        localStorage.setItem(TV_TOKEN_KEY, token);
        setTvToken(token);
    }, []);

    const enterFullscreen = useCallback(() => {
        const el = containerRef.current ?? document.documentElement;
        el.requestFullscreen?.().then(() => setIsFullscreen(true)).catch(() => {});
    }, []);

    const exitFullscreen = useCallback(() => {
        document.exitFullscreen?.().then(() => setIsFullscreen(false)).catch(() => {});
    }, []);

    useEffect(() => {
        if (tvMode && tvToken) {
            const t = setTimeout(enterFullscreen, 800);
            return () => clearTimeout(t);
        }
    }, [tvMode, tvToken, enterFullscreen]);

    useEffect(() => {
        const h = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', h);
        return () => document.removeEventListener('fullscreenchange', h);
    }, []);

    const { data, isLoading, dataUpdatedAt, refetch, isFetching } = useFinanceTvData(tvMode, tvToken);

    useEffect(() => {
        const id = setInterval(() => setClock(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    const lastUpdate = useMemo(
        () => dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString('pt-BR') : '—',
        [dataUpdatedAt],
    );

    const s = data?.summary;
    const q = data?.queues;
    const payNow = q?.pay_now ?? [];
    const recentlyPaid = q?.recently_paid ?? [];

    if (!isPaired) return <TvPairingScreen onApproved={handleApproved} />;

    return (
        <div ref={containerRef} className="flex min-h-screen flex-col bg-zinc-950 text-white">
            {/* Faixa dourada topo */}
            <div
                className="h-1 w-full shrink-0"
                style={{ background: 'linear-gradient(90deg,#3D2D08 0%,#8B6914 20%,#C9A84C 50%,#F0D06A 65%,#C9A84C 80%,#8B6914 100%)' }}
            />

            <div className="flex flex-1 flex-col">

                {/* ── Header ─────────────────────────────────── */}
                <header className="flex shrink-0 items-center justify-between gap-4 border-b border-white/6 px-6 py-3">
                    <div className="flex items-center gap-4">
                        <img
                            src="/img/design/Logo_Buckler_OffWhite.png"
                            alt="Buckler"
                            className="h-8 object-contain"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                        <div>
                            <h1 className="text-lg font-black leading-none text-white">Painel de Pagamentos</h1>
                            <p className="text-[10px] text-gray-600">Atualiza a cada 30s · último: {lastUpdate}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <UsdTicker rates={data?.usd_brl} />

                        {/* Relógio */}
                        <div className="text-right">
                            <p className="font-mono text-2xl font-black tabular-nums text-white">
                                {clock.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </p>
                            <p className="text-[10px] capitalize text-gray-600">
                                {clock.toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </p>
                        </div>

                        {/* Controles */}
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => refetch()}
                                disabled={isFetching}
                                className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 p-2 text-gray-400 hover:bg-white/10 disabled:opacity-40"
                                title="Atualizar"
                            >
                                <PiArrowsClockwiseBold className={cn('size-4', isFetching && 'animate-spin')} />
                            </button>
                            <button
                                type="button"
                                onClick={isFullscreen ? exitFullscreen : enterFullscreen}
                                className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 p-2 text-gray-400 hover:bg-white/10"
                                title={isFullscreen ? 'Sair do fullscreen' : 'Tela cheia'}
                            >
                                <PiArrowsOutBold className="size-4" />
                            </button>
                            {tvMode ? (
                                <button
                                    type="button"
                                    onClick={() => { localStorage.removeItem(TV_TOKEN_KEY); setTvToken(null); exitFullscreen(); }}
                                    className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-gray-400 hover:bg-white/10"
                                >
                                    <PiArrowLeftBold className="size-3.5" /> Desparear
                                </button>
                            ) : (
                                <Link
                                    to="/calendar/containers"
                                    className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-gray-400 hover:bg-white/10"
                                >
                                    <PiArrowLeftBold className="size-3.5" /> Voltar
                                </Link>
                            )}
                        </div>
                    </div>
                </header>

                {/* ── KPI strip ──────────────────────────────── */}
                <div className="grid shrink-0 grid-cols-3 divide-x divide-white/6 border-b border-white/6">
                    {/* KPI: Pagar agora */}
                    <div className={cn(
                        'flex items-center gap-4 px-8 py-4',
                        s?.pay_now_count ? 'bg-red-950/30' : '',
                    )}>
                        <div className={cn(
                            'flex size-14 items-center justify-center rounded-2xl',
                            s?.pay_now_count ? 'bg-red-500/20' : 'bg-white/5',
                        )}>
                            <PiWarningCircleBold className={cn('size-8', s?.pay_now_count ? 'text-red-400' : 'text-gray-600')} />
                        </div>
                        <div>
                            <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Pagar agora</p>
                            <p className={cn('text-5xl font-black tabular-nums leading-none', s?.pay_now_count ? 'text-red-300' : 'text-white')}>
                                {s?.pay_now_count ?? 0}
                            </p>
                            <p className="mt-1 text-xs text-gray-600">invoice no financeiro</p>
                        </div>
                    </div>

                    {/* KPI: USD a pagar */}
                    <div className={cn(
                        'flex items-center gap-4 px-8 py-4',
                        s?.total_usd_pending ? 'bg-red-950/20' : '',
                    )}>
                        <div className={cn(
                            'flex size-14 items-center justify-center rounded-2xl',
                            s?.total_usd_pending ? 'bg-red-500/15' : 'bg-white/5',
                        )}>
                            <PiCurrencyDollarBold className={cn('size-8', s?.total_usd_pending ? 'text-red-400' : 'text-gray-600')} />
                        </div>
                        <div>
                            <p className="text-xs font-bold uppercase tracking-widest text-gray-500">USD a pagar</p>
                            <p className={cn('text-4xl font-black tabular-nums leading-none', s?.total_usd_pending ? 'text-red-300' : 'text-white')}>
                                {fmtUsd(s?.total_usd_pending)}
                            </p>
                            <p className="mt-1 text-xs text-gray-600">total pendente</p>
                        </div>
                    </div>

                    {/* KPI: Processos */}
                    <div className="flex items-center gap-4 px-8 py-4">
                        <div className="flex size-14 items-center justify-center rounded-2xl bg-emerald-500/10">
                            <PiCheckCircleBold className="size-8 text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Processos abertos</p>
                            <p className="text-5xl font-black tabular-nums leading-none text-emerald-300">
                                {s?.open_processes ?? 0}
                            </p>
                            <p className="mt-1 text-xs text-gray-600">em andamento</p>
                        </div>
                    </div>
                </div>

                {isLoading && !data ? (
                    <div className="flex flex-1 items-center justify-center">
                        <p className="text-xl text-gray-500">Carregando painel…</p>
                    </div>
                ) : (
                    <div className="flex flex-1 flex-col overflow-hidden">

                        {/* ── Cabeçalho da tabela ──────────────── */}
                        <div className="flex shrink-0 items-center gap-4 border-b border-white/6 bg-zinc-900/80 px-5 py-2">
                            <div className="flex items-center gap-2.5">
                                <span className={cn(
                                    'size-2 rounded-full',
                                    payNow.length > 0 ? 'animate-pulse bg-red-500' : 'bg-gray-700',
                                )} />
                                <span className="text-xs font-black uppercase tracking-widest text-red-300">
                                    Pagar Agora
                                </span>
                                <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-xs font-black text-red-300">
                                    {payNow.length}
                                </span>
                            </div>
                            {payNow.length === 0 && (
                                <span className="text-sm text-gray-600">Nenhum pagamento pendente ✓</span>
                            )}

                            {/* Colunas header */}
                            {payNow.length > 0 && (
                                <div className="ml-auto flex items-center gap-4 text-[9px] font-bold uppercase tracking-widest text-gray-700">
                                    <span className="w-[260px]">Cliente</span>
                                    <span className="w-[100px] text-center">Pedido</span>
                                    <span className="w-[130px] text-right">Total</span>
                                    <span className="w-[90px] text-center">Progresso</span>
                                    <span className="flex-1">1° Pgto</span>
                                    <span className="flex-1">2° Pgto</span>
                                </div>
                            )}
                        </div>

                        {/* ── Linhas do scoreboard ─────────────── */}
                        <div className="flex-1 overflow-y-auto">
                            {payNow.map((item, idx) => (
                                <ScoreRow key={item.id} item={item} idx={idx} />
                            ))}
                        </div>

                        {/* ── Pagos recentemente ───────────────── */}
                        {recentlyPaid.length > 0 && (
                            <div className="shrink-0 border-t border-emerald-500/15 bg-emerald-950/20 px-5 py-2">
                                <div className="flex items-center gap-3 overflow-x-auto">
                                    <div className="flex shrink-0 items-center gap-2">
                                        <span className="size-1.5 rounded-full bg-emerald-400" />
                                        <span className="text-xs font-black text-emerald-400">Pagos</span>
                                    </div>
                                    {recentlyPaid.map((item) => (
                                        <div
                                            key={item.id}
                                            className="flex shrink-0 items-center gap-2 rounded-lg border border-[#C9A84C]/15 bg-[#C9A84C]/5 px-3 py-1.5"
                                        >
                                            <BrandAvatar name={item.client} logoUrl={item.client_logo_url} color="#C9A84C" size="sm" dark />
                                            <div>
                                                <p className="text-xs font-black text-white">{item.order_number}</p>
                                                <p className="text-[10px] font-semibold text-emerald-400">
                                                    {item.payment_label} · {fmtDate(item.paid_at)}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
